import type { AdminAuthConfig, AdminMe, TelegramLogin } from '@trendator/shared';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Loading } from '../components/ui';
import { Wordmark } from '../components/Wordmark';
import { api, errorText } from '../lib/api';
import { useLoad } from '../lib/hooks';

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramLogin) => void;
  }
}

/** Вход: Telegram Login Widget (домен админки должен быть привязан к боту в @BotFather → /setdomain). */
export function Login({ onLogin }: { onLogin: (me: AdminMe) => void }) {
  const cfg = useLoad(() => api.get<AdminAuthConfig>('/auth/config'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const bot = cfg.data?.botUsername;

  useEffect(() => {
    if (!bot || !box.current) return;
    window.onTelegramAuth = async (user) => {
      setError(null);
      try {
        onLogin(await api.post<AdminMe>('/auth/telegram', user));
      } catch (err) {
        setError(errorText(err));
      }
    };
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://telegram.org/js/telegram-widget.js?22';
    s.dataset.telegramLogin = bot;
    s.dataset.size = 'large';
    s.dataset.radius = '12';
    s.dataset.onauth = 'onTelegramAuth(user)';
    s.dataset.requestAccess = 'write';
    box.current.replaceChildren(s);
    return () => {
      delete window.onTelegramAuth;
    };
  }, [bot, onLogin]);

  const dev = async () => {
    setBusy(true);
    try {
      onLogin(await api.post<AdminMe>('/auth/dev'));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="flex w-full max-w-[400px] flex-col items-center gap-6 p-8 text-center">
        <Wordmark />
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-2xl font-extrabold">Вход в админку</h1>
          <p className="m-0 text-sm text-muted">Только для Telegram-аккаунтов из белого списка.</p>
        </div>
        {cfg.loading ? <Loading /> : null}
        {cfg.error ? <p className="m-0 text-sm text-bad">{cfg.error}</p> : null}
        <div ref={box} className="min-h-10" />
        {cfg.data && !bot ? <p className="m-0 text-sm text-warn">Вход через Telegram не настроен: задай BOT_USERNAME.</p> : null}
        {cfg.data?.devLogin ? (
          <Button variant="dashed" onClick={() => void dev()} busy={busy} className="w-full">
            Войти без Telegram (dev)
          </Button>
        ) : null}
        {error ? <p className="m-0 text-sm text-bad" role="alert">{error}</p> : null}
      </Card>
    </div>
  );
}
